import { create, fromBinary, toBinary } from '@bufbuild/protobuf';

import { WsRequest } from 'APP/wsApi/wsAppApi/requests/request.types';
import {
  type SecretChatAccessRequest,
  SecretChatAccessRequest_Status,
} from 'APP/wsApi/wsAppApi/types/chat/chat_model_pb';
import {
  AcceptSecretChatAccessRequestsRequest,
  AcceptSecretChatAccessRequestsRequestSchema,
  AddSecretChatParticipantsRequest,
  AddSecretChatParticipantsRequestSchema,
  CreateE2eeCommitRequest,
  CreateE2eeCommitRequestSchema,
  CreateSecretChatRequest,
  CreateSecretChatRequestSchema,
  RemoveSecretChatParticipantsRequest,
  RemoveSecretChatParticipantsRequestSchema,
} from 'APP/wsApi/wsAppApi/types/chat/chat_requests_pb';
import {
  E2eeAddParticipant,
  E2eeAddParticipant_WelcomeSchema,
  E2eeAddParticipantSchema,
  E2eeCommit,
  E2eeWelcome,
  KeyPackage,
  KeyPackage_Algorithm,
  KeyPackageSchema,
  UserDeviceKeyPackage,
} from 'APP/wsApi/wsAppApi/types/encryption/encryption_model_pb';
import { UploadKeyPackagesRequest } from 'APP/wsApi/wsAppApi/types/encryption/encryption_requests_pb';
import {
  GetCommitsSuccess,
  GetWelcomesSuccess,
  GetKeyPackagesForUntrustedDeviceSuccess,
} from 'APP/wsApi/wsAppApi/types/encryption/encryption_response_pb';
import {
  EncryptedMessagePayloadData,
  EncryptedMessagePayloadData_TextData,
  EncryptedMessagePayloadData_TextDataSchema,
  EncryptedMessagePayloadDataSchema,
} from 'APP/wsApi/wsAppApi/types/message/message_encrypted_model_pb';

import {
  E2eePayloadType,
  IAddSecretGroupParticipantsData,
  ICreateCommitForSecretGroupData,
  ICreateSecretChatData,
  IE2eeAddParticipantWelcome,
  IE2eeCommit,
  IE2eeCommitResponse,
  IE2eeMessagePayload,
  IE2eeTextMessagePayload,
  IE2eeWelcome,
  IE2eeWelcomeResponse,
  IKeyPackage,
  ISecretChatAccessRequest,
  ISecretGroupParticipantChangeRequest,
  IUserDeviceKeyPackage,
  IUserDeviceKeyPackages,
  IUserDeviceKeyPackagesResponse,
  KeyPackageAlgorithm,
  SecretChatAccessRequestStatus,
} from './e2eeModel.types';

export const keyPackageAlgorithmMap: Record<KeyPackage_Algorithm, KeyPackageAlgorithm> = {
  [KeyPackage_Algorithm.CURVE_25519]: KeyPackageAlgorithm.CURVE_25519,
  [KeyPackage_Algorithm.ED_448]: KeyPackageAlgorithm.ED_448,
};

export const keyPackageAlgorithmRequestMap: Record<KeyPackageAlgorithm, KeyPackage_Algorithm> = {
  [KeyPackageAlgorithm.CURVE_25519]: KeyPackage_Algorithm.CURVE_25519,
  [KeyPackageAlgorithm.ED_448]: KeyPackage_Algorithm.ED_448,
};

export const secretChatAccessRequestStatusResponseMap: Record<
  SecretChatAccessRequest_Status,
  SecretChatAccessRequestStatus
> = {
  [SecretChatAccessRequest_Status.REQUESTED]: SecretChatAccessRequestStatus.Requested,
  [SecretChatAccessRequest_Status.ACCEPTED]: SecretChatAccessRequestStatus.Accepted,
  [SecretChatAccessRequest_Status.DECLINED]: SecretChatAccessRequestStatus.Declined,
};

export const secretChatAccessRequestStatusRequestMap: Record<
  SecretChatAccessRequestStatus,
  SecretChatAccessRequest_Status
> = {
  [SecretChatAccessRequestStatus.Requested]: SecretChatAccessRequest_Status.REQUESTED,
  [SecretChatAccessRequestStatus.Accepted]: SecretChatAccessRequest_Status.ACCEPTED,
  [SecretChatAccessRequestStatus.Declined]: SecretChatAccessRequest_Status.DECLINED,
};

export function mapKeyPackageRequest(data: IKeyPackage): KeyPackage {
  return create(KeyPackageSchema, {
    algorithm: keyPackageAlgorithmRequestMap[data.algorithm],
    data: new Uint8Array(data.data),
  });
}

export function mapKeyPackageRequests(data: IKeyPackage[]): WsRequest<UploadKeyPackagesRequest> {
  return {
    keyPackages: data.map(mapKeyPackageRequest),
    algorithm: keyPackageAlgorithmRequestMap[KeyPackageAlgorithm.CURVE_25519],
  };
}

export function mapKeyPackage(data: KeyPackage): IKeyPackage {
  return {
    algorithm: keyPackageAlgorithmMap[data.algorithm],
    data: data.data.buffer.slice(data.data.byteOffset),
  };
}

export function mapUserDeviceKeyPackageResponse(data: UserDeviceKeyPackage): IUserDeviceKeyPackage {
  return {
    userId: String(data.userId),
    deviceId: String(data.deviceId),
    keyPackage: mapKeyPackage(data.keyPackage!),
  };
}

export function mapKeyPackagesForUntrustedResponse(
  data: GetKeyPackagesForUntrustedDeviceSuccess
): IUserDeviceKeyPackagesResponse {
  return {
    keyPackages: data.keyPackages.map(mapUserDeviceKeyPackageResponse),
    cursor: data.nextCursor,
  };
}

export function mapKeyPackagesResponse(
  keyPackages: UserDeviceKeyPackage[]
): IUserDeviceKeyPackages {
  return {
    keyPackages: keyPackages.map(mapUserDeviceKeyPackageResponse),
  };
}

export function mapCreateSecretGroupAndInviteRequestParticipants(
  data: IE2eeAddParticipantWelcome[]
): E2eeAddParticipant[] {
  const sortWelcomesToParticipants = data.reduce((acc, item) => {
    if (acc[item.userId]) {
      acc[item.userId].welcomes.push(
        create(E2eeAddParticipant_WelcomeSchema, {
          deviceId: item.deviceId,
          epoch: item.epoch,
          payload: new Uint8Array(item.payload),
        })
      );
    } else {
      acc[item.userId] = create(E2eeAddParticipantSchema, {
        participantId: BigInt(item.userId),
        welcomes: [
          {
            deviceId: item.deviceId,
            epoch: item.epoch,
            payload: new Uint8Array(item.payload),
          },
        ],
      });
    }
    return acc;
  }, {} as { [key: string]: E2eeAddParticipant });

  return Object.values(sortWelcomesToParticipants);
}

export function mapCreateSecretGroupAndInviteRequest(
  data: ICreateSecretChatData
): WsRequest<CreateSecretChatRequest> {
  return create(CreateSecretChatRequestSchema, {
    name: data.name ?? undefined,
    description: data.description ?? undefined,
    avatarUrl: data.avatarUrl ?? undefined,
    participants: mapCreateSecretGroupAndInviteRequestParticipants(
      data.participantsWelcomeMessages
    ),
    admins: [],
    commit: {
      epoch: data.epoch,
      payload: new Uint8Array(data.commit),
    },
  });
}

export function mapAddSecretGroupParticipantsRequest(
  data: IAddSecretGroupParticipantsData
): WsRequest<AddSecretChatParticipantsRequest> {
  return create(AddSecretChatParticipantsRequestSchema, {
    chatId: BigInt(data.groupId),
    participants: mapCreateSecretGroupAndInviteRequestParticipants(
      data.participantsWelcomeMessages
    ),
    commit: {
      epoch: data.epoch,
      payload: new Uint8Array(data.commit),
    },
  });
}

export function mapCreateCommitRequest(
  data: ICreateCommitForSecretGroupData
): WsRequest<CreateE2eeCommitRequest> {
  return create(CreateE2eeCommitRequestSchema, {
    chatId: BigInt(data.groupId),
    participants: mapCreateSecretGroupAndInviteRequestParticipants(
      data.participantsWelcomeMessages
    ),
    commit: {
      epoch: data.epoch,
      payload: new Uint8Array(data.commit),
    },
  });
}

export function mapAcceptSecretChatAccessRequestsRequest(
  data: IAddSecretGroupParticipantsData
): WsRequest<AcceptSecretChatAccessRequestsRequest> {
  return create(AcceptSecretChatAccessRequestsRequestSchema, {
    chatId: BigInt(data.groupId),
    participant: mapCreateSecretGroupAndInviteRequestParticipants(data.participantsWelcomeMessages),
    commit: {
      epoch: data.epoch,
      payload: new Uint8Array(data.commit),
    },
  });
}

export function mapActiveSecretChatAccessRequestsResponse(
  data: SecretChatAccessRequest
): ISecretChatAccessRequest {
  console.log(data);
  return {
    chatId: data.chatId.toString(),
    status: secretChatAccessRequestStatusResponseMap[data.status],
    userId: data.userId.toString(),
    deviceId: data.deviceId,
    osName: data.device!.osName,
    osVersion: data.device!.osVersion,
    deviceName: data.device!.device,
  };
}

export function mapRemoveSecretGroupParticipantsRequest(
  data: ISecretGroupParticipantChangeRequest
): WsRequest<RemoveSecretChatParticipantsRequest> {
  return create(RemoveSecretChatParticipantsRequestSchema, {
    chatId: BigInt(data.groupId),
    userIds: data.userIds.map((userId) => BigInt(userId)),
    commit: {
      epoch: BigInt(data.commit.epoch),
      payload: new Uint8Array(data.commit.payload),
    },
  });
}

export function mapWelcomeResponse(data: E2eeWelcome): IE2eeWelcome {
  return {
    deviceId: data.deviceId,
    chatId: String(data.chatId),
    epoch: Number(data.epoch),
    payload: data.payload.buffer.slice(data.payload.byteOffset),
  };
}

export function mapWelcomesResponse(data: GetWelcomesSuccess): IE2eeWelcomeResponse {
  return {
    nextCursor: data.nextCursor,
    welcomes: data.welcomes.map(mapWelcomeResponse),
  };
}

export function mapCommitResponse(data: E2eeCommit): IE2eeCommit {
  return {
    payload: data.payload.buffer.slice(data.payload.byteOffset),
    epoch: Number(data.epoch),
  };
}

export function mapCommitsResponse(data: GetCommitsSuccess): IE2eeCommitResponse {
  return {
    nextCursor: data.nextCursor,
    commits: data.commits.map(mapCommitResponse),
  };
}

function mapE2eeTextMessagePayload(
  payload: EncryptedMessagePayloadData_TextData
): IE2eeTextMessagePayload {
  return {
    payloadType: E2eePayloadType.RichText,
    text: payload.text,
  };
}

export function mapIE2eeMessagePayload(payload: ArrayBuffer): IE2eeMessagePayload {
  const decodedPayload = fromBinary(EncryptedMessagePayloadDataSchema, new Uint8Array(payload));

  switch (decodedPayload.payload.case) {
    case 'text':
      return mapE2eeTextMessagePayload(decodedPayload.payload.value);
    default:
      throw new Error(`Unknown message payload: ${decodedPayload.payload.case}`);
  }
}

function mapToSendE2eeTextMessagePayload(
  payload: IE2eeTextMessagePayload
): EncryptedMessagePayloadData_TextData {
  return create(EncryptedMessagePayloadData_TextDataSchema, {
    text: payload.text,
  });
}

export function mapToSendE2eeMessagePayloadData(payload: IE2eeMessagePayload): Uint8Array {
  let createdPayload: EncryptedMessagePayloadData | null = null;

  switch (payload.payloadType) {
    case E2eePayloadType.RichText:
      createdPayload = create(EncryptedMessagePayloadDataSchema, {
        payload: {
          case: 'text',
          value: mapToSendE2eeTextMessagePayload(payload as IE2eeTextMessagePayload),
        },
      });
      break;
    default:
      throw new Error(`Unknown payload type: ${payload.payloadType}`);
  }

  return toBinary(EncryptedMessagePayloadDataSchema, createdPayload);
}
